import React from "react";
import { Grid, Typography, Autocomplete, TextField } from "@mui/material";
import { Field } from "formik";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import FormSection from "./FormSection";
import { upperCaseChange } from "../utils/inputHelpers";
import { EXISTING_TP_INSURERS, existingTpMode } from "../utils/companyRules";

const INSURER_OPTIONS = EXISTING_TP_INSURERS.map((c) => ({ label: c, value: c }));

const ErrorLine = ({ name, errors, touched }) => (
  <div className="errorMessage mb-2 mt-2">{errors[name] && touched[name] ? <div>{errors[name]}</div> : " "}</div>
);

/**
 * The customer's EXISTING third-party (TP) policy — Reliance's own-damage product asks for it (2026-10-22).
 *   - OD only (Reliance): TP Insurer (KSHEMA / National), TP Policy Number, TP Start Date, TP End Date — all required.
 *   - Any other policy, Multiple company included: nothing is shown (the owner, 2026-10-23). A two-insurer policy's TP
 *     is bought by us, so its number and dates are entered once the TP part is finished: Edit -> Status or
 *     Edit -> TP policy details.
 */
const ExistingTpPolicySection = ({ values, errors, touched, setFieldValue }) => {
  if (existingTpMode(values) !== "required") return null;

  const dateField = (name, label) => (
    <Grid item xs={12} sm={6} md={3}>
      <Typography sx={{ mb: 1, fontWeight: "bold" }}>{label} *</Typography>
      <DatePicker
        name={name}
        className="Date_Picker w-100"
        value={values[name] ? dayjs(values[name]) : null}
        onChange={(d) => setFieldValue(name, d ? d.toDate() : "")}
        placeholder={label}
        format="DD/MM/YYYY"
        getPopupContainer={(trigger) => trigger.parentElement}
        changeOnBlur={false}
        popupStyle={{ zIndex: 9999 }}
      />
      <ErrorLine name={name} errors={errors} touched={touched} />
    </Grid>
  );

  return (
    <FormSection
      testId="existing-tp-section"
      title="Third party (TP) policy"
      description={
        <span data-testid="existing-tp-hint">
          The customer's current TP policy. Reliance asks for it on the own damage policy.
        </span>
      }
    >
        <Grid container columnSpacing={3}>

          <Grid item xs={12} sm={6} md={3}>
            <Typography sx={{ mb: 1, fontWeight: "bold" }}>TP Insurer *</Typography>
            <Autocomplete
              name="existingTpInsurer"
              className="AutoComplete_InputBox"
              disablePortal
              value={INSURER_OPTIONS.find((o) => o.value === values.existingTpInsurer) || null}
              options={INSURER_OPTIONS}
              onChange={(e, v) => setFieldValue("existingTpInsurer", v ? v.value : "")}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              renderInput={(params) => <TextField {...params} placeholder="KSHEMA or National" />}
            />
            <ErrorLine name="existingTpInsurer" errors={errors} touched={touched} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Typography sx={{ mb: 1, fontWeight: "bold" }}>TP Policy Number *</Typography>
            <Field
              name="existingTpPolicyNumber"
              placeholder="TP Policy Number"
              className="textField w-100"
              onChange={upperCaseChange(setFieldValue, "existingTpPolicyNumber")}
            />
            <ErrorLine name="existingTpPolicyNumber" errors={errors} touched={touched} />
          </Grid>

          {dateField("existingTpStartDate", "TP Start Date")}
          {dateField("existingTpEndDate", "TP End Date")}
        </Grid>
    </FormSection>
  );
};

export default ExistingTpPolicySection;
